import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Speed Match — FTUE demo (3 essais progressive intro).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SYMBOLS } from './params';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'speed_match.demo.step1_title',
        hintKey: 'speed_match.demo.step1_hint',
        setSize: 4,
        soaMs: 1100,
        scenario: 'nomatch',
    },
    {
        idx: 2,
        titleKey: 'speed_match.demo.step2_title',
        hintKey: 'speed_match.demo.step2_hint',
        setSize: 4,
        soaMs: 800,
        scenario: 'match',
    },
    {
        idx: 3,
        titleKey: 'speed_match.demo.step3_title',
        hintKey: 'speed_match.demo.step3_hint',
        setSize: 6,
        soaMs: 700,
        scenario: 'nomatch',
    },
];
const FIELD = 240;
function pickPair(setSize, scenario) {
    const pool = SYMBOLS.slice(0, setSize);
    const prev = pool[Math.floor(Math.random() * pool.length)];
    if (scenario === 'match') {
        return { prev, current: prev };
    }
    const others = pool.filter((s) => s !== prev);
    const current = others[Math.floor(Math.random() * others.length)] ?? prev;
    return { prev, current };
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing_prev');
    const [pick, setPick] = useState(null);
    const pairRef = useRef(pickPair(DEMO_STEPS[0].setSize, DEMO_STEPS[0].scenario));
    const showTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const isMatch = pairRef.current.prev === pairRef.current.current;
    const correct = pick !== null && (pick === 'match') === isMatch;
    useEffect(() => {
        if (phase === 'showing_prev') {
            showTimerRef.current = setTimeout(() => {
                setPhase('showing_current');
            }, step.soaMs);
        }
        else if (phase === 'showing_current') {
            showTimerRef.current = setTimeout(() => {
                setPhase('awaiting');
            }, step.soaMs);
        }
        return () => {
            if (showTimerRef.current !== null)
                clearTimeout(showTimerRef.current);
        };
    }, [phase, step]);
    const handlePick = (p) => {
        if (phase !== 'awaiting')
            return;
        setPick(p);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        const next = DEMO_STEPS[stepIdx + 1];
        pairRef.current = pickPair(next.setSize, next.scenario);
        setStepIdx((i) => i + 1);
        setPhase('showing_prev');
        setPick(null);
    };
    const displaySymbol = phase === 'showing_prev'
        ? pairRef.current.prev
        : phase === 'showing_current'
            ? pairRef.current.current
            : null;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('speed_match.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex items-center justify-center", style: { width: FIELD, height: FIELD, background: '#1a1a1a' }, children: [displaySymbol !== null && (_jsx("span", { className: "font-display text-[80px] leading-none text-cyan-surface", children: displaySymbol })), phase === 'awaiting' && (_jsx("span", { className: "font-display text-[16px] text-cyan-surface", children: t('speed_match.question') })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[40px] font-medium',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: correct ? '✓' : '✗' }))] }), phase === 'awaiting' && (_jsxs("div", { className: "flex w-full max-w-[320px] gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('match'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('speed_match.buttons.match') }), _jsx("button", { type: "button", onClick: () => handlePick('nomatch'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-media font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:opacity-90", children: t('speed_match.buttons.nomatch') })] })), phase === 'feedback' && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('speed_match.feedback.correct')
                    : t('speed_match.feedback.incorrect') })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map